import { Box } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackAnalytics } from "@/lib/analytics";
import { getPostcodeMultiplier, skipSizes } from "@/lib/pricing";
import { formatCurrency, cn } from "@/lib/utils";
import type { SkipSize } from "@/types/booking";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";

export function SkipSizeStep({ state, isLocked, updateState, goNext, goBack }: StepProps) {
  function selectSkipSize(skipSize: SkipSize) {
    updateState({ skipSize });
    trackAnalytics("skip size selected", { skipSize, postcode: state.postcode });
    goNext();
  }

  return (
    <StepFrame
      title="Choose skip size"
      description="Prices are mocked today but isolated so the cards can be fed by a live pricing API later."
      canBack
      onBack={goBack}
      hideNext
      isReadOnly={isLocked}
      mobileSummary={state.pricingResult ? `Current total ${formatCurrency(state.pricingResult.totalPayable)}` : undefined}
    >
      <div className="grid gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
        {skipSizes.map((skip) => {
          const isSelected = state.skipSize === skip.id;
          const isLockedAlternative = isLocked && !isSelected;

          return (
            <Card
              key={skip.id}
              className={cn(
                "overflow-hidden rounded-lg transition-all",
                isSelected && "border-primary bg-primary/5",
                isLockedAlternative && "border-muted bg-muted/20 shadow-none",
              )}
            >
              <CardContent className="flex h-full p-0 sm:p-0 md:flex-col">
                <div className={cn("flex w-20 shrink-0 items-center justify-center bg-secondary md:aspect-video md:w-full", isLockedAlternative && "bg-muted/50")}>
                  <Box className={cn("h-8 w-8 text-accent md:h-10 md:w-10", isLockedAlternative && "text-muted-foreground/45")} />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 md:gap-4 md:p-5">
                  <div className="space-y-1 md:space-y-2">
                    <h3 className={cn("text-base font-black md:text-lg", isLockedAlternative && "text-muted-foreground/60")}>{skip.title}</h3>
                    <p className={cn("line-clamp-2 text-sm text-muted-foreground md:line-clamp-none", isLockedAlternative && "text-muted-foreground/45")}>{skip.description}</p>
                  </div>
                  <div className="mt-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className={cn("text-lg font-black md:text-xl", isLockedAlternative && "text-muted-foreground/50")}>
                      {formatCurrency(Math.round(skip.basePrice * getPostcodeMultiplier(state.postcode)))}
                    </p>
                    <Button
                      type="button"
                      className={cn(
                        isSelected && isLocked && "disabled:opacity-100",
                        isLockedAlternative && "border-muted bg-white/40 text-muted-foreground/50 shadow-none disabled:opacity-100",
                      )}
                      variant={isSelected ? "default" : "outline"}
                      onClick={() => selectSkipSize(skip.id)}
                      disabled={isLocked}
                    >
                      {isSelected ? "Selected" : "Select"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </StepFrame>
  );
}
